import { gql, TypedDocumentNode } from '@common/hooks';
import { CommentInfoFragment, CommentInfoFragmentType } from '@common/graphql';

type FileType = {
  id: string;
  filename: string;
  createdAt: string;
  downloadUrl?: string;
};

export type StaffingRequestApplicationsType = {
  id: string;
  state: string;
  stateChangedAt: string;
  rejectionReason?: string;
  rejectionReasonDescription?: string;
  rejectionDetails?: string;
  availableToStartAt?: string;
  timeoffDescription?: string;
  interviewAvailability?: string;
  doNotRehire: boolean;
  doNotRehireComment?: string;
  activeOffer?: {
    id: string;
    state: string;
    stateChangedAt: string;
    staffingRequestAssignment: {
      id: string;
    };
  };
  comments: CommentInfoFragmentType[];
  worksSubmittalPacket?: {
    id: string;
    files: FileType[];
  };
  worksProfile: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;

    worksSubmittalPackets?: {
      id: string;
      archived: boolean;
      files: FileType[];
    }[];
  };
  canWithdraw: {
    value: boolean;
  };
  canResubmit: {
    value: boolean;
  };
  staffingRequest: {
    id: string;
    code: string;
    startDate: string;
    endDate: string;
    length: string;
    name: string;
    shiftType: 'day' | 'evening' | 'night' | 'variable';
    shiftsPerWeek: number;
    hoursPerShift: number;
    hiringLeader: {
      id: string;
      name: string;
    };
    role: {
      id: string;
      abbreviation: string;
    };
    organizationBusiness: {
      id: string;
      name: string;
      city: string;
      stateCode: string;
    };
    organizationUnit: {
      id: string;
      name: string;
      clinicalUnit: {
        id: string;
        displayName: string;
      };
    };
  };
  supplier: {
    id: string;
    name: string;
    timeZone: string;
  };
  organization: {
    id: string;
    name: string;
  };
};

export type StaffingRequestStatesType =
  | 'accepted'
  | 'submitted'
  | 'submittal_packet_change_requested'
  | 'in_review'
  | 'offered'
  | 'rejected'
  | 'withdrawn';

export type PaginatedCandidateSubmissionsQueryVariables = {
  limit: number;
  offset: number;
  orderDirection?: string;
  orderField?: string;
  worksProfileId?: string;
  states?: StaffingRequestStatesType[];
  filters?: Record<string, string[] | null>;
};

export type PaginatedCandidateSubmissionsQueryResponse = {
  paginatedStaffingRequestApplications: {
    staffingRequestApplications: StaffingRequestApplicationsType[];
    totalCount: number;
  };
};

export const PaginatedCandidateSubmissionsQuery: TypedDocumentNode<
  PaginatedCandidateSubmissionsQueryResponse,
  PaginatedCandidateSubmissionsQueryVariables
> = gql`
  query PaginatedCandidateSubmissionsQuery(
    $limit: Int!
    $offset: Int!
    $orderDirection: OrderDirection
    $orderField: StaffingRequestApplicationOrderField
    $worksProfileId: String
    $states: [StaffingRequestApplicationState!]
    $filters: StaffingRequestApplicationFilterInput
  ) {
    paginatedStaffingRequestApplications(
      limit: $limit
      offset: $offset
      orderDirection: $orderDirection
      orderField: $orderField
      worksProfileId: $worksProfileId
      states: $states
      filters: $filters
    ) {
      staffingRequestApplications {
        id
        state
        stateChangedAt
        rejectionReason
        rejectionReasonDescription
        rejectionDetails
        availableToStartAt
        timeoffDescription
        interviewAvailability
        doNotRehire
        doNotRehireComment
        activeOffer {
          id
          state
          stateChangedAt
          staffingRequestAssignment {
            id
          }
        }
        comments {
          id
          ...CommentInfoFragment
        }
        worksSubmittalPacket {
          id
          files {
            id
            filename
            downloadUrl
            createdAt
          }
        }
        worksProfile {
          id
          firstName
          lastName
          fullName

          worksSubmittalPackets {
            id
            archived
            files {
              id
              filename
              downloadUrl
              createdAt
            }
          }
        }
        canWithdraw {
          value
        }
        canResubmit {
          value
        }
        staffingRequest {
          id
          code
          startDate
          endDate
          length
          name
          shiftType
          shiftsPerWeek
          hoursPerShift
          hiringLeader {
            id
            name
          }
          role {
            id
            abbreviation
          }
          organizationBusiness {
            id
            name
            city
            stateCode
          }
          organizationUnit {
            id
            name
            clinicalUnit {
              id
              displayName
            }
          }
        }
        supplier {
          id
          name
          timeZone
        }
        organization {
          id
          name
        }
      }
      totalCount
    }
  }
  ${CommentInfoFragment}
`;
